import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConfirmDialogAction } from '@/components/admin/ConfirmDialogAction';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { adminImpersonationApi } from '@/api/admin/adminImpersonationApi';
import { adminUsersApi } from '@/api/admin/adminUsersApi';
import type { AdminUser } from '@/types/admin.model';
import { SubscriptionRole } from '@/types/user.model';
import { useAuthStore } from '@/store/authStore';
import { handleGlobalError } from '@/utils/error-handler';
import { MoreHorizontal } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type PendingAction = {
  title: string;
  description: string;
  /** Replaces the generic success toast when "applied" is the wrong wording. */
  successToast?: { title: string; description: string };
  /**
   * Skips the `onDone` refresh. Set by actions that hand the session over to
   * somebody else: `onDone` refetches from ADMIN-only endpoints, which the
   * borrowed token cannot reach.
   */
  skipRefresh?: boolean;
  run: () => Promise<void>;
} | null;

interface Props {
  user: AdminUser;
  onDone: () => void;
}

export const UserActionsMenu = ({ user, onDone }: Props) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const startImpersonation = useAuthStore((state) => state.startImpersonation);
  const [pending, setPending] = useState<PendingAction>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isUnbanning, setIsUnbanning] = useState(false);
  // A ref, not the state above: two clicks inside one tick both read the state
  // as false, because React has not re-rendered between them — which is exactly
  // the double click this guards against. The state is only there to drive the
  // label and the disabled attribute.
  const isUnbanningRef = useRef(false);

  const isOwner = (user.roles ?? []).includes(SubscriptionRole.OWNER);

  // The backend always refuses to impersonate another admin, so the menu does
  // not offer it.
  const isAdmin = (user.roles ?? []).includes(SubscriptionRole.ADMIN);
  // Banned and deleted accounts open a support session for review, but the
  // backend refuses a takeover on them.
  const isViewOnly = user.banned || !!user.deleted;

  const confirmAndRun = async () => {
    if (!pending) return;
    setIsRunning(true);
    try {
      await pending.run();
      toast(
        pending.successToast ?? {
          title: 'Listo',
          description: 'La acción se aplicó correctamente.',
        },
      );
      // Navigating away inside `run` does not save us from this: React 18
      // schedules that re-render instead of flushing it, so everything below
      // still runs on the caller's synchronous turn. `onDone` would fire its
      // ADMIN-only refetch with the borrowed token already installed, collect a
      // 403 and drop a "no se pudo cargar" toast over the screen just entered.
      if (!pending.skipRefresh) {
        onDone();
      }
    } catch (error) {
      handleGlobalError(error, 'No se pudo completar la acción', 'Intentá de nuevo en un momento.');
    } finally {
      setIsRunning(false);
      setPending(null);
    }
  };

  // Unbanning is reversible and low impact, so it skips the confirmation dialog.
  // It still needs the in-flight guard the dialog gives every other action:
  // Radix keeps the menu open until the click finishes bubbling, so two quick
  // clicks send two PATCH /users/{pk}/unban and write two UNBAN_USER rows for
  // one decision. The action undoes; the audit trail is what someone later
  // reads to reconstruct what happened, and that does not.
  const unban = async () => {
    if (isUnbanningRef.current) return;
    isUnbanningRef.current = true;
    setIsUnbanning(true);
    try {
      await adminUsersApi.unbanUser(user.pk);
      toast({ title: 'Usuario desbaneado' });
      onDone();
    } catch (error) {
      handleGlobalError(error, 'No se pudo desbanear', 'Intentá de nuevo en un momento.');
    } finally {
      isUnbanningRef.current = false;
      setIsUnbanning(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Acciones">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isOwner && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  title: 'Asignar rol OWNER',
                  description: `${user.email} va a poder crear y administrar clubes.`,
                  run: () => adminUsersApi.grantOwnerRole(user.email),
                })
              }
            >
              Asignar OWNER
            </DropdownMenuItem>
          )}
          {isOwner && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  title: 'Quitar rol OWNER',
                  description:
                    `${user.email} deja de poder administrar clubes. Los clubes donde sea ` +
                    'el único dueño quedan deshabilitados y se desvinculan; los que tengan ' +
                    'otro dueño siguen funcionando. Si alguno tiene un torneo en curso, la ' +
                    'acción se rechaza: hay jugadores inscriptos.',
                  run: () => adminUsersApi.revokeOwnerRole(user.pk),
                })
              }
            >
              Quitar OWNER
            </DropdownMenuItem>
          )}
          {!isAdmin && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  title: 'Entrar como este usuario',
                  description:
                    `Vas a ver la aplicación como ${user.email}, en modo lectura. ` +
                    (isViewOnly
                      ? 'La cuenta está suspendida o eliminada: vas a poder mirar, pero ' +
                        'no tomar el control.'
                      : 'Para poder hacer cambios vas a tener que tomar el control desde ' +
                        'el cartel, y eso queda registrado en la auditoría.'),
                  successToast: {
                    title: 'Sesión de soporte iniciada',
                    description: `Estás viendo la aplicación como ${user.email}, en modo lectura.`,
                  },
                  skipRefresh: true,
                  run: async () => {
                    const result = await adminImpersonationApi.impersonate(user.pk);
                    const started = startImpersonation(result);
                    // Defence in depth: the store is the single authority on
                    // whether a session was installed, and it refuses to replace
                    // one. This screen is not actually reachable with a support
                    // session active — AdminGuard gates it on the ADMIN role,
                    // which the borrowed token does not carry — so this branch
                    // should never run; it is kept because it is cheap and it
                    // fails closed. Thrown rather than returned: a dialog that
                    // closes leaving the admin exactly where they were reads as a
                    // dead menu item.
                    if (!started) {
                      throw new Error(
                        'Ya hay una sesión de soporte activa. Salí de ella desde el cartel ' +
                          'inferior y volvé a intentarlo.',
                      );
                    }
                    navigate('/inicio');
                  },
                })
              }
            >
              Entrar como
            </DropdownMenuItem>
          )}
          {!isViewOnly && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  title: 'Banear usuario',
                  description:
                    `${user.email} no va a poder iniciar sesión, y si tiene una sesión abierta ` +
                    'se le corta en el próximo request. Sus inscripciones y pagos no se tocan.',
                  run: () => adminUsersApi.banUser(user.pk),
                })
              }
            >
              Banear
            </DropdownMenuItem>
          )}
          {user.banned && (
            <DropdownMenuItem disabled={isUnbanning} onClick={unban}>
              {isUnbanning ? 'Desbaneando...' : 'Desbanear'}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRunning}>Cancelar</AlertDialogCancel>
            <ConfirmDialogAction
              onConfirm={confirmAndRun}
              isRunning={isRunning}
              runningLabel="Aplicando..."
            >
              Confirmar
            </ConfirmDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
